/* Report categories (MOP, MOS, ...): the company group a report appears
 * under on the TV Reports menu. Admins manage the list
 * (/admin/report-categories); owners and viewers only ever see the
 * category_id / category_name carried on each report, so for them the list
 * is derived from the reports themselves. */
import { useQuery } from "@tanstack/react-query";
import { Tag } from "lucide-react";
import { useMemo } from "react";

import { adminApi } from "../../api/admin";
import type { Mode } from "../../shell/role";
import type { Report, ReportCategory } from "../../types";

export const CATEGORIES_KEY = ["report-categories"] as const;

/** Gallery chip value: every report, only uncategorized ones, or one category id. */
export type CategoryFilter = "all" | "none" | (string & {});
export const CATEGORY_FILTER_STORAGE = "mrp-reports-category";

export function sortCategories<T extends { name: string; sort_order: number }>(list: T[]): T[] {
  return list.slice().sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
}

export function useReportCategories(mode: Mode, reports?: Report[]) {
  const admin = useQuery({
    queryKey: CATEGORIES_KEY,
    queryFn: adminApi.listReportCategories,
    enabled: mode === "admin",
    staleTime: 30_000,
    retry: false,
  });
  const categories = useMemo<ReportCategory[]>(() => {
    if (mode === "admin" && admin.data) return sortCategories(admin.data);
    // Owners/viewers (or an admin before the list loads): what the reports carry.
    const seen = new Map<string, ReportCategory>();
    for (const r of reports ?? []) {
      if (!r.category_id) continue;
      const c = seen.get(r.category_id) ?? { id: r.category_id, name: r.category_name ?? "Category", sort_order: 0, report_count: 0 };
      c.report_count++;
      seen.set(r.category_id, c);
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [mode, admin.data, reports]);
  return { categories, isLoading: mode === "admin" && admin.isLoading, isError: admin.isError, error: admin.error };
}

export function readStoredCategoryFilter(): CategoryFilter {
  try {
    return (window.localStorage.getItem(CATEGORY_FILTER_STORAGE) as CategoryFilter) || "all";
  } catch {
    return "all";
  }
}

export function storeCategoryFilter(v: CategoryFilter): void {
  try {
    window.localStorage.setItem(CATEGORY_FILTER_STORAGE, v);
  } catch {
    // Private window / blocked storage: the chip just isn't remembered.
  }
}

/** Small tag showing a report's category (or a dashed "No category"). */
export function CategoryTag({ name, showNone }: { name?: string | null; showNone?: boolean }) {
  if (!name) return showNone ? <span className="ds-cat-tag ds-cat-tag--none" title="Not in a category yet — it appears under Uncategorized">No category</span> : null;
  return (
    <span className="ds-cat-tag" title={`Category: ${name}`}>
      <Tag size={11} />
      {name}
    </span>
  );
}
